import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Pin, CheckCircle, Cancel } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ResetPasswordConfirmDTO } from '../dtos';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../utils/passwordValidation';

interface ConfirmPasswordResetFormProps {
  email: string;
  onSubmit: (data: ResetPasswordConfirmDTO) => Promise<void>;
  onCancel: () => void;
  onResendCode: () => void;
  isLoading?: boolean;
  error?: string;
  success?: boolean;
}

interface ConfirmPasswordResetFormData {
  code: string;
  password: string;
  confirmPassword: string;
}

export const ConfirmPasswordResetForm: React.FC<ConfirmPasswordResetFormProps> = ({
  email,
  onSubmit,
  onCancel,
  onResendCode,
  isLoading = false,
  error,
  success = false,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [passwordFocused, setPasswordFocused] = React.useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<ConfirmPasswordResetFormData>({
    mode: 'onChange',
    defaultValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');
  const passwordValidation = React.useMemo(() => validatePassword(password || ''), [password]);

  const handleFormSubmit = async (data: ConfirmPasswordResetFormData) => {
    try {
      await onSubmit({
        email,
        code: data.code,
        password: data.password,
      });
      reset();
    } catch (error) {
      console.error('Password reset confirmation failed:', error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Password Reset Successful
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your password has been updated. You can now sign in with your new password.
          </Typography>
          <Button variant="contained" onClick={onCancel} sx={{ mt: 2 }}>
            Go to Login
          </Button>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: 'fit-content',
          py: 2,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom textAlign="center">
          Reset Your Password
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={1}>
          We sent a verification code to
        </Typography>
        <Typography variant="body2" fontWeight={600} textAlign="center" mb={2}>
          {email}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {error.includes('expired') && (
              <Link
                component="button"
                type="button"
                onClick={onResendCode}
                underline="hover"
                sx={{ display: 'block', mt: 1, cursor: 'pointer' }}
              >
                Request a new code
              </Link>
            )}
          </Alert>
        )}

        <Controller
          name="code"
          control={control}
          rules={{
            required: 'Verification code is required',
            pattern: {
              value: /^\d{6}$/,
              message: 'Code must be 6 digits',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Verification Code"
              error={!!errors.code}
              helperText={errors.code?.message}
              margin="dense"
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric',
                style: { fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '0.3em' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Pin />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: 'Password is required',
            validate: (value) => {
              const validation = validatePassword(value);
              if (!validation.isValid) {
                return 'Password does not meet requirements';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                margin="dense"
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {(passwordFocused || password) && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                    Password Requirements:
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.minLength ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="At least 8 characters"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.hasLetter ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="Contains at least one letter"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, minHeight: 28 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {passwordValidation.checks.hasNumberOrSymbol ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="Contains at least one number or special character"
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {PASSWORD_HELPER_TEXT}
                  </Typography>
                </Box>
              )}
            </>
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            size="large"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </Button>
        </Box>

        <Box textAlign="center" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Didn&apos;t receive a code?{' '}
            <Link
              component="button"
              type="button"
              onClick={onResendCode}
              underline="hover"
              disabled={isLoading}
              sx={{ cursor: 'pointer' }}
            >
              Resend Code
            </Link>
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};
